import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { t } from "../../lib/i18n";

export default function HeroCarousel({ banners }) {
  const slides = banners && banners.length ? banners : [];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const len = slides.length;
  const advance = useCallback(
    (dir = 1) => setIndex((i) => (i + dir + len) % len),
    [len],
  );

  useEffect(() => {
    if (!len || paused) return undefined;
    const id = setInterval(() => advance(1), 6000);
    return () => clearInterval(id);
  }, [advance, len, paused]);

  if (!len) {
    return (
      <div
        data-testid="hero-carousel-empty"
        className="mx-auto max-w-6xl px-5 py-20 text-sm text-[#1A1A1A]/60 sm:px-6"
      >
        {t("generic.empty")}
      </div>
    );
  }

  const current = slides[index];

  return (
    <section
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative w-full overflow-hidden bg-[#1A1A1A]"
    >
      <div className="relative min-h-[360px] w-full sm:min-h-[480px]">
        {slides.map((s, i) => (
          <div
            key={s.id || i}
            className={[
              "absolute inset-0 transition-opacity duration-700 ease-out",
              i === index ? "opacity-100" : "opacity-0",
            ].join(" ")}
            aria-hidden={i !== index}
          >
            <img
              src={s.image_url}
              alt={s.headline || "ShutterKART hero"}
              className="h-full w-full object-cover"
              loading={i === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/85 via-[#0a0a0a]/45 to-transparent" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex min-h-[360px] max-w-6xl flex-col justify-end px-5 pb-14 pt-24 sm:min-h-[480px] sm:px-6 sm:pb-20 sm:pt-32">
          <p
            data-testid="hero-eyebrow"
            className="mb-4 text-[10px] uppercase tracking-[0.32em] text-[#C8A24B] sm:mb-6 sm:text-xs"
          >
            {t("brand.name")}
          </p>
          <h1
            data-testid="hero-headline"
            className="max-w-3xl font-display text-3xl leading-tight text-[#FAF7F2] sm:text-5xl lg:text-6xl"
          >
            {current.headline}
          </h1>
          {current.subheadline ? (
            <p
              data-testid="hero-subheadline"
              className="mt-4 max-w-xl text-sm text-[#FAF7F2]/80 sm:mt-6 sm:text-base"
            >
              {current.subheadline}
            </p>
          ) : null}
          <Link
            to={current.cta_route || "/plan"}
            data-testid="hero-cta"
            className="mt-8 inline-flex w-fit items-center rounded-full bg-[#C8A24B] px-7 py-3 text-xs uppercase tracking-[0.22em] text-[#1A1A1A] transition hover:bg-[#dbb359] sm:mt-10"
          >
            {current.cta_label || t("home.cta.button")}
          </Link>
        </div>

        {len > 1 ? (
          <>
            <div className="absolute bottom-6 right-5 z-20 flex items-center gap-3 sm:right-6">
              <button
                type="button"
                data-testid="hero-prev"
                aria-label="Previous slide"
                onClick={() => advance(-1)}
                className="rounded-full border border-[#FAF7F2]/30 bg-[#1A1A1A]/40 p-2 text-[#FAF7F2] transition hover:border-[#C8A24B] hover:text-[#C8A24B]"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="hero-next"
                aria-label="Next slide"
                onClick={() => advance(1)}
                className="rounded-full border border-[#FAF7F2]/30 bg-[#1A1A1A]/40 p-2 text-[#FAF7F2] transition hover:border-[#C8A24B] hover:text-[#C8A24B]"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="absolute bottom-6 left-5 z-20 flex items-center gap-2 sm:left-6">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`hero-dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={[
                    "h-[3px] w-8 rounded-full transition",
                    i === index ? "bg-[#C8A24B]" : "bg-[#FAF7F2]/30 hover:bg-[#FAF7F2]/60",
                  ].join(" ")}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
